/* Aritmética de superficies del formulario de informe (RF-E1 §8.2).
 *
 * Vive fuera del componente para poder probarla como función pura: dentro del
 * árbol, el único caso que la pantalla ejercita con facilidad es "dos números
 * bien escritos", que no distingue una suma correcta de una que ignora las
 * filas vacías o que no entiende la coma decimal. */

/**
 * Interpreta lo que el usuario tipeó en un campo de superficie.
 *
 * Acepta COMA como separador decimal —el placeholder de la pantalla dice
 * "Ej: 12,5" y en Chile se escribe así— además del punto. Devuelve `null` para
 * lo que no es un número, que es distinto de `0`: una fila recién agregada
 * está vacía, no vale cero hectáreas.
 */
export function parsearSuperficie(valor: string | null | undefined): number | null {
  if (valor === null || valor === undefined) return null;

  const limpio = valor.trim().replace(",", ".");
  if (limpio === "") return null;

  const numero = Number(limpio);
  return Number.isFinite(numero) ? numero : null;
}

/**
 * Suma las superficies de los roles, ignorando las filas sin número.
 *
 * Suma en CENTÉSIMAS y divide al final: en punto flotante `0.1 + 0.2` no da
 * `0.3`, y esta suma se muestra al lado de la superficie declarada para que el
 * usuario las compare. Un desvío de representación haría aparecer una
 * diferencia que no existe.
 */
export function sumarSuperficies(valores: (string | null | undefined)[]): number {
  const centesimas = valores.reduce<number>((total, valor) => {
    const numero = parsearSuperficie(valor);
    return numero === null ? total : total + Math.round(numero * 100);
  }, 0);

  return centesimas / 100;
}

/**
 * Cómo se escribe la unidad del informe en pantalla: `há` o `m2`.
 *
 * Vive acá y no repetida en cada pantalla: la tilde de "há" y el "m2" sin
 * superíndice son decisiones del producto —las mismas que imprime el PDF— y
 * dos ternarios sueltos se separan el día que una de las dos cambie.
 */
export function etiquetaDeUnidad(unidad: "ha" | "m2" | null | undefined): string {
  return unidad === "m2" ? "m2" : "há";
}

/** Formato chileno: coma decimal y punto de miles. */
export function formatearSuperficie(valor: number): string {
  return new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(valor);
}

/**
 * Si la suma de los roles difiere de la superficie declarada.
 *
 * RF-E1 §8.2 pide ADVERTIR la diferencia, nunca bloquear ("la coincidencia NO
 * es condición restrictiva"). Con la superficie total sin completar no hay
 * nada contra qué comparar, así que no se advierte: el usuario todavía la está
 * cargando y un aviso ahí sería ruido, no ayuda.
 */
/**
 * La superficie declarada del predio, EN HECTÁREAS, venga en la unidad que
 * venga.
 *
 * La unidad vive en el INFORME y no en cada rol (un total no puede sumar 8.000
 * m² con 23 ha), así que el número solo no dice cuánto mide el predio. Quien
 * lo use para multiplicar un valor UF/ha tiene que convertir: con "m2" sin
 * convertir, un predio de 8.000 m² se leería como ocho mil hectáreas y el valor
 * total del predio saldría diez mil veces más grande, en una pantalla que
 * ninguna cifra contradice.
 *
 * `null` cuando no está declarada: un informe puede estar `incompleto`
 * (RF-E1 §8.3) y un `0` se leería como una medición.
 */
export function superficieEnHectareas(
  valor: string | null | undefined,
  unidad: "ha" | "m2" | null | undefined,
): number | null {
  const numero = parsearSuperficie(valor);
  if (numero === null) return null;

  return unidad === "m2" ? numero / 10_000 : numero;
}
